// Two devices on one note (docs/SYNC.md §3.8) — pure. The open editor (web NotePane, phone NoteScreen) uses these to
// decide what a body saved elsewhere means for what's on screen, when to apply it, and what the header says.
//
// A body chunk carries `sid` (this save's id) and `base` (the sid it was built on). `rev` only counts saves — two
// devices saving from the same body both write rev N+1, so it can't tell a newer body from a concurrent one.
import { merge3 } from './merge3.js';

export const LIVE_IDLE_MS = 1500;      // no keystroke for this long = the editor is clean; a held remote body merges
export const PUSH_IDLE_MS = 2000;      // push (syncNow) this long after typing stops
export const KNOWN_MAX = 24;           // bodies an editor remembers by sid (its own saves, what it merged)

/** A fresh save id. */
export function bodySid(now = Date.now()) {
  return `${now.toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** The body's lineage fields, as read from a chunk ({ sid, base } → nulls when an older writer left them out). */
export const bodyLineage = (d) => ({ sid: d?.sid || null, base: d?.base || null });

const same = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
const ident = (n) => Array.from({ length: n }, (_, i) => i);

/**
 * Merge a body saved elsewhere into the editor, block by block (block lists in the editor's own serialisation).
 * base = the body both last agreed on, mine = the editor now, theirs = the remote body. Returns
 * { outcome, blocks, map, conflicts }:
 *   'none'     theirs is what's on screen — nothing to do
 *   'local'    only this side changed — keep the editor, it saves
 *   'remote'   only they changed — theirs replaces the editor (map keeps the cursor's block)
 *   'merged'   both changed, different blocks — both kept
 *   'conflict' both changed the same block — mine stays in that block; theirs must be filed whole as a conflict copy
 * Never drops text: a conflicting block of theirs is in the conflict copy, everything else is in `blocks`.
 */
export function mergeBody(base = [], mine = [], theirs = []) {
  if (same(mine, theirs)) return { outcome: 'none', blocks: mine, map: ident(mine.length), conflicts: 0 };
  if (same(theirs, base)) return { outcome: 'local', blocks: mine, map: ident(mine.length), conflicts: 0 };
  if (same(mine, base)) return { outcome: 'remote', blocks: theirs, map: merge3(base, mine, theirs).map, conflicts: 0 };
  const r = merge3(base, mine, theirs, { onConflict: 'mine' });
  return { outcome: r.conflicts ? 'conflict' : 'merged', blocks: r.blocks, map: r.map, conflicts: r.conflicts };
}

/** Plain-text bodies (the phone without the rich editor): lines are the blocks. */
export function mergePlain(base = '', mine = '', theirs = '') {
  const lines = (t) => String(t || '').split('\n');
  const r = mergeBody(lines(base), lines(mine), lines(theirs));
  return { ...r, text: r.blocks.join('\n') };
}

/**
 * What to merge a remote body against.
 *   known  = Map sid → html of bodies this editor has held (loaded, saved, merged)
 *   agreed = { sid, html } — the body this editor last agreed on
 *   remote = { sid, base, html }
 * → { skip: true } (ours echoing back, or already merged) | { baseHtml, concurrent }
 * `concurrent`: theirs was built on an older body than ours — it overwrote our save on the server; merging against
 * the body both were built on brings ours back.
 */
export function remoteBase({ known, agreed, remote }) {
  if (remote.sid && known.has(remote.sid)) return { skip: true };
  if (remote.base && known.has(remote.base)) return { baseHtml: known.get(remote.base), concurrent: remote.base !== agreed?.sid };
  return { baseHtml: agreed?.html ?? '', concurrent: false };
}

/** Remember a body by sid (bounded, oldest out). */
export function rememberBody(known, sid, html) {
  if (!sid) return known;
  known.delete(sid);
  known.set(sid, html);
  while (known.size > KNOWN_MAX) known.delete(known.keys().next().value);
  return known;
}

/**
 * Hold-while-typing. `typed()` on every keystroke / editor change; `offer(remote)` when a body arrives →
 * { action: 'apply', remote } now, or { action: 'hold', wait } (call `poll()` after `wait` ms). Only the latest held
 * body is kept — it already contains the earlier ones. A body is never held longer than `maxHoldMs` (typing without a
 * pause for that long): saves wait for the merge, so it can't wait forever — the merge itself is atomic in the editor.
 */
export const MAX_HOLD_MS = 8000;
export function createLiveBody({ now = Date.now, idleMs = LIVE_IDLE_MS, maxHoldMs = MAX_HOLD_MS } = {}) {
  let lastKeyAt = -Infinity;
  let held = null;
  let heldSince = 0;
  const typing = (t) => t - lastKeyAt < idleMs;
  const wait = (t) => Math.max(1, Math.min(idleMs - (t - lastKeyAt), heldSince + maxHoldMs - t));
  const hold = (remote, t) => { if (!held) heldSince = t; held = remote; return { action: 'hold', wait: wait(t) }; };
  return {
    typed(t = now()) { lastKeyAt = t; },
    typing(t = now()) { return typing(t); },
    offer(remote, t = now()) {
      if (typing(t) && !(held && t - heldSince >= maxHoldMs)) return hold(remote, t);
      held = null;
      return { action: 'apply', remote };
    },
    /** Keep `remote` for later regardless of typing (the editor was busy) → poll after `ms`. */
    defer(remote, ms, t = now()) { if (!held) heldSince = t; held = remote; return { action: 'hold', wait: ms }; },
    poll(t = now()) {
      if (!held) return { action: 'none' };
      if (typing(t) && t - heldSince < maxHoldMs) return { action: 'hold', wait: wait(t) };
      const remote = held; held = null;
      return { action: 'apply', remote };
    },
    held() { return held; },
    clear() { held = null; },
  };
}

/**
 * The note header's status line. → { key, label, tone: 'ok'|'busy'|'warn'|'bad'|'live' }
 *   conflict  "Edited on another device"     (until reviewed)
 *   saving    "Saving…"                      (typed, not saved yet / save in flight)
 *   updated   "Updated from another device"  (a remote body merged in the last few seconds)
 *   saved     "Saved · synced" | "Saved · syncing…" | "Saved · offline" | "Saved · not synced" (a rejected op)
 */
export function noteSyncLabel({ conflict = false, saving = false, error = false, mergedAt = 0, sync = {}, online = true, now = Date.now(), flashMs = 4000 } = {}) {
  if (conflict) return { key: 'conflict', label: 'Edited on another device', tone: 'warn' };
  if (error) return { key: 'error', label: 'Not saved — retrying on next edit', tone: 'bad' };
  if (saving) return { key: 'saving', label: 'Saving…', tone: 'busy' };
  if (mergedAt && now - mergedAt < flashMs) return { key: 'updated', label: 'Updated from another device', tone: 'live' };
  if (sync.failed > 0) return { key: 'unsynced', label: 'Saved · not synced', tone: 'bad' };
  if (!online || sync.state === 'offline') return { key: 'offline', label: 'Saved · offline', tone: 'warn' };
  if (sync.pending > 0 || sync.state === 'syncing') return { key: 'syncing', label: 'Saved · syncing…', tone: 'busy' };
  return { key: 'synced', label: 'Saved · synced', tone: 'ok' };
}
